import React, { createContext, useContext, useEffect, useRef } from 'react';
import * as _ from 'lodash';
import moment from 'moment';
import { useStateWithGetter } from '@bld/bld-libs/utils';
import { TARGET_PARTNER_COMPANY_TYPE } from '@bld/bld-libs/constants';

import FieldBulletinBoardsApi from './FieldBulletinBoardsApi';
import FieldBulletinBoardsService from './FieldBulletinBoardsService';

import LocalStorageService from '../utilities/LocalStorageService';
import SessionStorageService from '../utilities/SessionStorageService';
import _masterSlideLoader from '../../utilities/masterSlideLoader';
import { messageUtility } from '../../utilities/messageUtility';
import {
  deleteControlCode,
  trimAndRemoveLineBreak,
} from '../../utilities/stringUtil';
import { isPartnerCompanyRoleId } from '../../utilities/roleUtility';

import {
  BACK_EVENT_SOURCE,
  BOARD_FILE,
  DATE_TIME_FORMAT,
  IMPORTANCE_TYPE,
  IS_SEND_MAIL,
  STATUS_CODE,
} from '../../utilities/constants';

const SAVE_MODE_TYPE = {
  UPDATE: 0,
  PUBLISH: 1,
};

const FieldBulletinBoardEditCtx = createContext();

function FieldBulletinBoardEditProvider({
  children,
  fieldBulletinBoardId,
  fieldBulletinBoardSearchParam = {},
}) {
  const [, setFieldInfo, getFieldInfo] = useStateWithGetter({});
  const [data, setData, getData] = useStateWithGetter({
    bulletin_board_publication_status: undefined,
    importance_type: IMPORTANCE_TYPE.UNCHECK,
    title: '',
    contents: '',
    publication_start_at: '',
    target_partner_company_type: TARGET_PARTNER_COMPANY_TYPE.ALL.TYPE_NUMBER,
    is_send_mail: IS_SEND_MAIL.UNCHECK,
    lock_version: undefined,
    field_bulletin_board_documents: [],
  });
  const [, setUploadFiles, getUploadFiles] = useStateWithGetter({});
  const [, setFormErrorMessage, getFormErrorMessage] = useStateWithGetter({});
  const [, setModalDisplayInfo, getModalDisplayInfo] = useStateWithGetter({});
  const [, setErrorDialogProperty, getErrorDialogProperty] = useStateWithGetter(
    {}
  );
  const [, setButtonDisableFlg, getButtonDisableFlg] = useStateWithGetter(
    false
  );
  const [, setBackEventSource, getBackEventSource] = useStateWithGetter('');
  const [, setLoadingFlg, getLoadingFlg] = useStateWithGetter(false);
  const [usageEndDate, setUsageEndDate, getUsageEndDate] = useStateWithGetter(
    undefined
  );
  const fileRef = useRef(null);

  /**
   * 初回表示時
   * */
  useEffect(() => {
    // 協力会社ユーザの場合
    const roleId = LocalStorageService.getRoleId();
    if (isPartnerCompanyRoleId(roleId)) {
      return window.location.replace('/error-permission');
    }

    FieldBulletinBoardsService.loadFieldInfo()
      .then(response => {
        if (
          _.isEmpty(response) ||
          !response.isAccessible ||
          !response.isEditable
        ) {
          return window.location.replace('/error-permission');
        }

        setFieldInfo(response.fieldInfo);
        setUsageEndDate(response.fieldInfo.usage_end_date);

        const fieldId = SessionStorageService.getFieldId();
        FieldBulletinBoardsApi.fetchFieldBulletinBoard(
          fieldId,
          fieldBulletinBoardId
        )
          .then(response => {
            if (!response) {
              return;
            }

            if (!response.result) {
              return window.location.replace('/error-500');
            }

            if (!response.data.can_display_edit_button) {
              return window.location.replace('/error-permission');
            }

            const fieldBulletinBoard =
              response.data.entities.field_bulletin_board;

            setData(prevData => ({
              ...prevData,
              bulletin_board_publication_status:
                fieldBulletinBoard.bulletin_board_publication_status,
              importance_type: fieldBulletinBoard.importance_type,
              title: fieldBulletinBoard.title,
              contents: fieldBulletinBoard.contents,
              publication_start_at: moment(
                fieldBulletinBoard.publication_start_at
              ).format(DATE_TIME_FORMAT.slashDateFormat),
              target_partner_company_type:
                fieldBulletinBoard.target_partner_company_type,
              is_send_mail: fieldBulletinBoard.is_send_mail,
              field_bulletin_board_documents: fieldBulletinBoard.documents,
              lock_version: fieldBulletinBoard.lock_version,
            }));
            setUploadFiles(
              _.map(fieldBulletinBoard.documents, document => {
                return { name: document.file_name, alreadyUpload: true };
              })
            );
          })
          .catch(() => {
            return window.location.replace('/error-500');
          });
      })
      .catch(error => {
        return window.location.replace('/error-permission');
      });
  }, []);

  /**
   * Buildee利用終了日取得時
   * */
  useEffect(() => {
    if (getUsageEndDate() === undefined) {
      return;
    }
    validateFormItem('publication_start_at');
  }, [usageEndDate]);

  /**
   * 完了系ダイアログを閉じる
   * */
  const closeCompleteDialog = () => {
    setModalDisplayInfo({ [_.findKey(getModalDisplayInfo())]: false });

    // 現場掲示板詳細に戻る
    _masterSlideLoader.show('FieldBulletinBoardDetail', {
      state: { id: fieldBulletinBoardId, fieldBulletinBoardSearchParam },
    });
  };

  /**
   * 確認系ダイアログを閉じる
   * */
  const closeConfirmDialog = () => {
    setModalDisplayInfo({ [_.findKey(getModalDisplayInfo())]: false });
  };

  /**
   * 現場掲示板削除
   * 削除確認ダイアログの実行時にコール
   * */
  const deleteFieldBulletinBoard = async () => {
    if (getLoadingFlg()) {
      return;
    }
    setLoadingFlg(true);
    await FieldBulletinBoardsApi.deleteFieldBulletinBoard(
      SessionStorageService.getFieldId(),
      fieldBulletinBoardId,
      { lock_version: getData().lock_version }
    )
      .then(response => {
        if (!response) {
          return;
        }

        if (response.result) {
          setModalDisplayInfo({
            [_.findKey(getModalDisplayInfo())]: false,
            deleteComplete: true,
          });
          return;
        }

        if (
          [STATUS_CODE.badRequest, STATUS_CODE.conflict].includes(
            response.status
          )
        ) {
          openErrorDialog(
            messageUtility.getMessage('E-000-167-02'),
            response.data.message
          );
          return;
        }
        return window.location.replace('/error-500');
      })
      .finally(() => {
        setLoadingFlg(false);
      });
  };

  /**
   * 編集画面を閉じるダイアログの編集をやめるボタン押下時
   * */
  const executeBack = () => {
    setModalDisplayInfo({ back: false });

    if (getBackEventSource() == BACK_EVENT_SOURCE.BREADCRUMBS) {
      setBackEventSource('');
      _masterSlideLoader.show('FieldBulletinBoardDetail', {
        state: {
          id: fieldBulletinBoardId,
          fieldBulletinBoardSearchParam,
        },
      });
    } else {
      setBackEventSource('');
      _masterSlideLoader.close();
    }
  };

  /**
   * 公開ボタン押下時
   * */
  const executePublish = () => {
    setModalDisplayInfo({ [_.findKey(getModalDisplayInfo())]: false });
    updateFieldBulletinBoard(SAVE_MODE_TYPE.PUBLISH);
  };

  /**
   * パンくず or マスターページヘッダーの閉じるボタン押下時
   * */
  const handleBack = backEventSource => {
    setBackEventSource(backEventSource);
    setModalDisplayInfo({ back: true });
  };

  /**
   * 入力フォーム値変更
   * @param {any}input : 各種入力値
   * */
  const handleChangeData = input => {
    if ('publication_start_at' in input && input.publication_start_at) {
      setData(prev => ({
        ...prev,
        publication_start_at: moment(input.publication_start_at).format(
          DATE_TIME_FORMAT.slashDateFormat
        ),
      }));
    } else {
      setData(prev => ({
        ...prev,
        ...input,
      }));
    }
  };

  /**
   * 削除ボタン押下時に削除ダイアログを表示
   * */
  const handleDeleteButtonClick = () => {
    setModalDisplayInfo({ delete: true });
  };

  /**
   * 削除完了ダイアログの「閉じる」ボタン押下時
   * */
  const handleDeleteCompleteModalCloseButtonClick = () => {
    setModalDisplayInfo({ deleteComplete: false });

    // 現場掲示板一覧に戻る
    _masterSlideLoader.show('FieldBulletinBoards', {
      state: {},
    });
  };

  /**
   * 削除確認ダイアログの「削除する」ボタン押下時
   * */
  const handleDeleteConfirmModalConfirmButtonClick = () => {
    setModalDisplayInfo({ [_.findKey(getModalDisplayInfo())]: false });
    deleteFieldBulletinBoard();
  };

  /**
   * ファイル削除時、画面からファイル名を削除し登録対象からも除外する
   * @param {string} key 対象項目名
   * */
  const handleFileDelete = async key => {
    const currentUploadFiles = getUploadFiles();
    // 画面表示対象から選択されたファイル名を除去
    setUploadFiles(
      _.filter(currentUploadFiles, (val, index) => {
        return index != key;
      })
    );

    // 登録用データから、削除されたファイルを除外する
    setData(prev => {
      return {
        ...prev,
        ...{
          field_bulletin_board_documents: _.filter(
            prev.field_bulletin_board_documents,
            (val, index) => {
              return key != index;
            }
          ),
        },
      };
    });

    const fileErrors = await FieldBulletinBoardsService.validateInputFile(
      getUploadFiles()
    );
    setFormErrorMessage(prev => {
      return {
        ...prev,
        ...{
          files: fileErrors,
        },
      };
    });

    setButtonDisableFlg(
      !FieldBulletinBoardsService.validate(getData(), getUsageEndDate())
        .status || _.some(fileErrors, fileError => !_.isEmpty(fileError))
    );
  };

  /**
   * ファイルアップロード時
   * @param {object} event :
   * */
  const handleFileUpload = async event => {
    const inputFile = event.target.files[0];

    // キャンセルされた場合は後続の処理を行わない
    if (!inputFile) {
      return;
    }

    const index = _.keys(getUploadFiles()).length;
    const newFiles = { ...getUploadFiles(), ...{ [index]: inputFile } };
    setUploadFiles(newFiles);

    const errorInfo = await FieldBulletinBoardsService.validateInputFile(
      newFiles
    );
    setFormErrorMessage(prev => ({
      ...prev,
      ...{ files: errorInfo },
    }));
    if (!_.isEmpty(errorInfo[index])) {
      setButtonDisableFlg(true);
      if (
        1 < errorInfo[index].length ||
        (errorInfo[index].length === 1 && index < BOARD_FILE.COUNT)
      ) {
        setData(prev => {
          return {
            ...prev,
            field_bulletin_board_documents: [
              ...prev.field_bulletin_board_documents,
              {},
            ],
          };
        });
        return;
      }
    }

    // アップロードファイルに対するエラーが無ければ、一時キーを取得する
    const fileData = FieldBulletinBoardsService.createFileData(inputFile);
    await FieldBulletinBoardsApi.registerTempFile(
      SessionStorageService.getFieldId(),
      fileData
    )
      .then(response => {
        if (!response) {
          return;
        }

        if (response.result) {
          const tempKeys = {
            field_bulletin_board_documents: [
              ...getData().field_bulletin_board_documents,
              {
                temp_file_key: response.data.entities.temp_file_key,
                temp_file_name: inputFile.name,
              },
            ],
          };
          setData(prev => {
            return { ...prev, ...tempKeys };
          });

          return;
        }

        return window.location.replace('/error-500');
      })
      .catch(() => {
        return window.location.replace('/error-500');
      });
  };

  /**
   * 公開ボタン押下時に公開予約確認ダイアログ or 公開確認ダイアログを表示
   * */
  const handlePublish = () => {
    if (hasError()) {
      return;
    }

    if (moment().isBefore(getData().publication_start_at)) {
      setModalDisplayInfo({ publishReserve: true });
    } else {
      setModalDisplayInfo({ publish: true });
    }
  };

  /**
   * 更新ボタン押下時
   * */
  const handleUpdateButtonClick = () => {
    if (hasError()) {
      return;
    }

    setModalDisplayInfo({ update: true });
  };

  /**
   * 更新確認ダイアログの「更新する」ボタン押下時
   * */
  const handleUpdateConfirmModalUpdateButtonClick = () => {
    updateFieldBulletinBoard(SAVE_MODE_TYPE.UPDATE);
  };

  /**
   * ボタン押下時にエラーの有無をチェックする
   * @return {boolean}
   * */
  const hasError = () => {
    const errorMessage = getFormErrorMessage();

    for (let key in errorMessage) {
      if (key === 'files') {
        if (hasFileError()) {
          return true;
        }
        continue;
      }

      if (!_.isEmpty(errorMessage[key])) {
        return true;
      }
    }

    const result = FieldBulletinBoardsService.validate(data, getUsageEndDate());
    setFormErrorMessage(prev => {
      return { ...prev, ...result.messages };
    });
    if (!_.isEmpty(result.messages)) {
      return true;
    }

    return false;
  };

  /**
   * ファイル関連エラーの有無をチェックする
   * @return {boolean}
   * */
  const hasFileError = () => {
    return (
      _.has(getFormErrorMessage(), 'files') &&
      _.some(getFormErrorMessage().files, file => !_.isEmpty(file))
    );
  };

  /**
   * エラーダイアログ表示
   * */
  const openErrorDialog = (title, errorMessage) => {
    setErrorDialogProperty({
      title: [title],
      message: `${messageUtility.getMessage('E-001-425')}
      ${errorMessage}`,
    });
    setModalDisplayInfo({
      [_.findKey(getModalDisplayInfo())]: false,
      error: true,
    });
  };

  /**
   * 現場掲示板更新
   * 公開予約確認ダイアログの予約・公開確認ダイアログの公開実行時にコール
   * @param {number} saveModeType 0:更新 1:公開
   * */
  const updateFieldBulletinBoard = async saveModeType => {
    if (getLoadingFlg()) {
      return;
    }

    setLoadingFlg(true);
    const params = FieldBulletinBoardsService.createParams(
      { ...getData() },
      saveModeType,
      false
    );
    await FieldBulletinBoardsApi.updateFieldBulletinBoard(
      SessionStorageService.getFieldId(),
      fieldBulletinBoardId,
      params
    )
      .then(response => {
        // request.js側でStatus Codeが401等でfalseを返すため
        if (!response) {
          return;
        }

        // 登録に成功した場合ダイアログを表示する
        if (response.result) {
          switch (saveModeType) {
            case SAVE_MODE_TYPE.UPDATE:
              setModalDisplayInfo({
                [_.findKey(getModalDisplayInfo())]: false,
                updateComplete: true,
              });
              return;
            case SAVE_MODE_TYPE.PUBLISH:
              setModalDisplayInfo({
                [_.findKey(getModalDisplayInfo())]: false,
                publishComplete: true,
              });
              return;
          }
        }

        if (
          [STATUS_CODE.badRequest, STATUS_CODE.conflict].includes(
            response.status
          )
        ) {
          switch (saveModeType) {
            case SAVE_MODE_TYPE.UPDATE:
              openErrorDialog(
                messageUtility.getMessage('E-000-167-01'),
                response.data.message
              );
              return;
            case SAVE_MODE_TYPE.PUBLISH:
              openErrorDialog(
                messageUtility.getMessage('E-000-165-04'),
                response.data.message
              );
              return;
          }
        }

        return window.location.replace('/error-500');
      })
      .catch(error => {
        return window.location.replace('/error-500');
      })
      .finally(() => {
        setLoadingFlg(false);
      });
  };

  /**
   * 各入力フォームのvalidationを実行(onBlur時)
   * @param {string} key 対象項目名
   * */
  const validateFormItem = key => {
    const params = getData();
    if (_.has(params, key)) {
      if (key === 'title') {
        params.title = deleteControlCode(trimAndRemoveLineBreak(params.title));
      }

      if (key === 'contents') {
        params.contents = deleteControlCode(params.contents.trim());
      }
    }

    setData(prevState => {
      return { ...prevState, ...params };
    });

    const result = FieldBulletinBoardsService.validate(
      getData(),
      getUsageEndDate()
    );

    setButtonDisableFlg(!result.status || hasFileError());

    if (!result.status && !_.isEmpty(result.messages[key])) {
      setFormErrorMessage(prevState => ({
        ...prevState,
        [key]: result.messages[key],
      }));
    } else {
      setFormErrorMessage(prevState => {
        return _.omit(prevState, [key]);
      });
    }
  };

  const contextValue = {
    closeCompleteDialog,
    closeConfirmDialog,
    executeBack,
    executePublish,
    fileRef,
    getButtonDisableFlg,
    getData,
    getErrorDialogProperty,
    getFormErrorMessage,
    getModalDisplayInfo,
    getUploadFiles,
    getUsageEndDate,
    handleBack,
    handleChangeData,
    handleDeleteButtonClick,
    handleDeleteCompleteModalCloseButtonClick,
    handleDeleteConfirmModalConfirmButtonClick,
    handleFileDelete,
    handleFileUpload,
    handlePublish,
    handleUpdateButtonClick,
    handleUpdateConfirmModalUpdateButtonClick,
    validateFormItem,
  };

  return (
    <FieldBulletinBoardEditCtx.Provider value={contextValue}>
      {children}
    </FieldBulletinBoardEditCtx.Provider>
  );
}

const useFieldBulletinBoardEdit = () => {
  return useContext(FieldBulletinBoardEditCtx);
};

export { FieldBulletinBoardEditProvider, useFieldBulletinBoardEdit };



// WEBPACK FOOTER //
// ./src/services/fieldBulletinBoard/FieldBulletinBoardEditContext.js